import React, { useState } from 'react';
import {
  Download,
  FileText,
  Eye,
  EyeOff,
  Printer,
  FileDown,
  FileText as FileIcon
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';

interface ReportViewerProps {
  content: {
    no_reference?: string;
    with_reference?: string;
  };
  taskId: string;
  reports?: {
    no_reference?: string;
    with_reference?: string;
  };
  onNewReport: () => void;
  onDownload: (
    reportType?: 'no_reference' | 'with_reference',
    format?: 'md' | 'pdf'
  ) => void;
}

const ReportViewer: React.FC<ReportViewerProps> = ({
  content,
  taskId,
  reports = {},
  onNewReport,
  onDownload
}) => {
  const [showPreview, setShowPreview] = useState(true);
  const [currentReportType, setCurrentReportType] = useState<'no_reference' | 'with_reference'>('no_reference');
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = (format: 'md' | 'pdf') => {
    onDownload(currentReportType, format);
    setShowDownloadMenu(false);
  };

  const currentContent = content?.[currentReportType] || '';
  const hasNoReference = content?.no_reference && content.no_reference.trim() !== '';
  const hasWithReference = content?.with_reference && content.with_reference.trim() !== '';
  const hasMultipleVersions = hasNoReference && hasWithReference;

  // 如果没有报告内容，显示加载状态
  if (!hasNoReference && !hasWithReference) {
    return (
      <div className="max-w-4xl mx-auto text-center">
        <div className="card">
          <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            报告内容加载中...
          </h3>
          <p className="text-gray-600 mb-4">
            正在获取完整的报告内容，请稍候
          </p>
          <p className="text-sm text-gray-500">
            任务ID: {taskId}
          </p>
          <div className="mt-4 flex justify-center space-x-4">
            <button
              onClick={() => onDownload(undefined, 'md')}
              className="btn-primary"
              disabled={!taskId}
            >
              <Download className="h-4 w-4 mr-2 inline" />
              下载报告
            </button>
            <button
              onClick={onNewReport}
              className="btn-secondary"
            >
              新建报告
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* 操作栏 */}
      <div className="card mb-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center">
            <FileText className="h-6 w-6 text-primary-600 mr-3" />
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                分析报告
              </h2>
              <p className="text-sm text-gray-600">
                任务ID: {taskId}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            {/* 报告类型切换 */}
            {hasMultipleVersions && (
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setCurrentReportType('no_reference')}
                  className={`px-3 py-1 text-sm rounded-md transition-colors ${
                    currentReportType === 'no_reference'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  标准版
                </button>
                <button
                  onClick={() => setCurrentReportType('with_reference')}
                  className={`px-3 py-1 text-sm rounded-md transition-colors ${
                    currentReportType === 'with_reference'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  参考版
                </button>
              </div>
            )}

            {/* 预览/源码切换 */}
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="btn-secondary flex items-center"
            >
              {showPreview ? <EyeOff className="h-4 w-4 mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
              {showPreview ? '源码' : '预览'}
            </button>

            {/* 打印 */}
            <button
              onClick={handlePrint}
              className="btn-secondary flex items-center"
            >
              <Printer className="h-4 w-4 mr-2" />
              打印
            </button>

            {/* 下载菜单 */}
            <div className="relative">
              <button
                onClick={() => setShowDownloadMenu(!showDownloadMenu)}
                className="btn-primary flex items-center"
              >
                <Download className="h-4 w-4 mr-2" />
                下载
              </button>
              {showDownloadMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border z-10">
                  <div className="p-2">
                    <p className="text-xs text-gray-500 px-2 py-1">
                      {currentReportType === 'no_reference' ? '标准版' : '参考版'}
                    </p>
                    <button
                      onClick={() => handleDownload('md')}
                      className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded flex items-center"
                    >
                      <FileIcon className="h-4 w-4 mr-2 text-gray-600" />
                      Markdown (.md)
                    </button>
                    <button
                      onClick={() => handleDownload('pdf')}
                      className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded flex items-center"
                    >
                      <FileDown className="h-4 w-4 mr-2 text-red-600" />
                      PDF (.pdf)
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* 快速下载按钮 */}
            <button
              onClick={() => onDownload(currentReportType, 'md')}
              className="btn-secondary flex items-center"
            >
              <Download className="h-4 w-4 mr-2" />
              {hasMultipleVersions
                ? currentReportType === 'with_reference'
                  ? '下载参考版'
                  : '下载标准版'
                : '下载报告'}
            </button>

            {/* 新建报告 */}
            <button
              onClick={onNewReport}
              className="btn-secondary"
            >
              新建报告
            </button>
          </div>
        </div>
      </div>

      {/* 报告内容 */}
      <div className="card">
        <div className="prose prose-lg max-w-none">
          {showPreview ? (
            <div className="markdown-content">
              <ReactMarkdown
                components={{
                  code({ className, children, ...props }: any) {
                    const match = /language-(\w+)/.exec(className || '');
                    const isInline = !match;

                    return !isInline ? (
                      <SyntaxHighlighter
                        style={oneLight as any}
                        language={match[1]}
                        PreTag="div"
                        {...props}
                      >
                        {String(children).replace(/\n$/, '')}
                      </SyntaxHighlighter>
                    ) : (
                      <code className={className} {...props}>
                        {children}
                      </code>
                    );
                  },
                  table({ children }) {
                    return (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          {children}
                        </table>
                      </div>
                    );
                  },
                  a({ href, children }) {
                    return (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-600 hover:text-primary-700"
                      >
                        {children}
                      </a>
                    );
                  },
                }}
              >
                {currentContent}
              </ReactMarkdown>
            </div>
          ) : (
            <pre className="bg-gray-50 p-4 rounded-lg overflow-x-auto text-sm">
              <code>{currentContent}</code>
            </pre>
          )}
        </div>
      </div>

      {/* 底部信息 */}
      <div className="mt-6 text-center text-sm text-gray-500">
        <p>报告生成完成时间: {new Date().toLocaleString()}</p>
        <p>如需修改报告内容，请重新生成或联系技术支持</p>
      </div>
    </div>
  );
};

export default ReportViewer;
